import styled from 'styled-components';
import { Card, CardTitle } from '@shared/ui/Card';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { useGetKadryRequestHistoryQuery } from '@shared/api';
import { KADRY_REQUEST_STATUS_LABELS, kadryDateTime } from '@entities/kadry';
import { KadryCardHead, KadryEmpty, KadryErrorBanner, KadryPerson } from '../ui';

const Timeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Step = styled.li`
  position: relative;
  padding-left: ${({ theme }) => theme.spacing(5)};

  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const What = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const Meta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const Reason = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1)};
  overflow-wrap: anywhere;
`;

interface Props {
  requestId: string;
}

/** История статусов заявки (Прил. Г): кто, когда и с какой причиной. */
export function HistoryPanel({ requestId }: Props) {
  const { data, isLoading, error, refetch } = useGetKadryRequestHistoryQuery(requestId);

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>История статусов</CardTitle>
      </KadryCardHead>
      {isLoading ? (
        <>
          <SkeletonLine />
          <SkeletonLine $width="60%" />
        </>
      ) : error ? (
        <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить историю" />
      ) : !data || data.length === 0 ? (
        <KadryEmpty>Переходов пока не было.</KadryEmpty>
      ) : (
        <Timeline>
          {[...data].reverse().map((h, i) => (
            <Step key={`${h.at}-${i}`}>
              <What>
                {h.from_status
                  ? `${KADRY_REQUEST_STATUS_LABELS[h.from_status]} → ${KADRY_REQUEST_STATUS_LABELS[h.to_status]}`
                  : `Создана: ${KADRY_REQUEST_STATUS_LABELS[h.to_status].toLowerCase()}`}
              </What>
              <Meta>
                {kadryDateTime(h.at)}
                {h.actor_id && (
                  <>
                    {' · '}
                    <KadryPerson id={h.actor_id} />
                  </>
                )}
              </Meta>
              {h.reason && <Reason>«{h.reason}»</Reason>}
            </Step>
          ))}
        </Timeline>
      )}
    </Card>
  );
}
